import type { GetOrderQuery } from "@server/gql/graphql";

const LOGIN_STORAGE_KEY = "voyado-onsite-login:v1";
const CHECKOUT_PATH_RE = /\/checkout(?:\/|$)/;

let loginSentFor: string | undefined;

type RedealIdentifyPayload = {
  email?: string;
  name?: string;
  phone?: string;
  contactId?: string;
};

type RedealCheckoutPayload = RedealIdentifyPayload & {
  total: string;
  revenue: string;
  currency: string;
  coupons: string[];
};

declare global {
  interface Window {
    redeal?: (
      tag: string,
      payload?: RedealIdentifyPayload | RedealCheckoutPayload,
    ) => void;
    redealHide?: () => void;
    IsRedealOpen?: (tag: string) => boolean;
  }
}

function canCallRedeal() {
  return typeof window !== "undefined" && typeof window.redeal === "function";
}

function isTagOpen(tag: string) {
  return typeof window.IsRedealOpen === "function" && window.IsRedealOpen(tag);
}

function compactIdentifyPayload(payload: RedealIdentifyPayload) {
  const next: RedealIdentifyPayload = {};
  if (payload.email) next.email = payload.email;
  if (payload.name) next.name = payload.name;
  if (payload.phone) next.phone = payload.phone;
  if (payload.contactId) next.contactId = payload.contactId;
  return Object.keys(next).length ? next : undefined;
}

function readLoginSessionId() {
  try {
    return sessionStorage.getItem(LOGIN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLoginSessionId(sessionId: string) {
  try {
    sessionStorage.setItem(LOGIN_STORAGE_KEY, sessionId);
  } catch {
    // Private mode can block sessionStorage. In-memory flag still limits this SPA session.
  }
}

export function whenRedealReady(onReady: () => void, timeoutMs = 4000) {
  let cancelled = false;

  const run = () => {
    if (!cancelled) onReady();
  };

  if (canCallRedeal()) {
    run();
    return () => {
      cancelled = true;
    };
  }

  if (typeof window === "undefined") {
    return () => {
      cancelled = true;
    };
  }

  const intervalId = window.setInterval(() => {
    if (canCallRedeal()) {
      cleanup();
      run();
    }
  }, 50);
  const timeoutId = window.setTimeout(cleanup, timeoutMs);

  function cleanup() {
    window.clearInterval(intervalId);
    window.clearTimeout(timeoutId);
  }

  return () => {
    cancelled = true;
    cleanup();
  };
}

export function hideVoyadoOnsite() {
  if (typeof window === "undefined") return;
  if (typeof window.redealHide === "function") {
    window.redealHide();
  }
}

export function showVoyadoCornerwidget(payload: RedealIdentifyPayload = {}) {
  if (!canCallRedeal()) return;
  window.redeal?.("cornerwidget", compactIdentifyPayload(payload));
}

export function sendVoyadoOnsiteLogin(payload: RedealIdentifyPayload) {
  if (!canCallRedeal()) return false;

  const data = compactIdentifyPayload(payload);
  if (!data) return false;

  const sessionId = data.contactId ?? "identified";
  if (loginSentFor === sessionId) return false;

  const storedSessionId = readLoginSessionId();
  if (storedSessionId === sessionId) {
    loginSentFor = sessionId;
    return false;
  }

  writeLoginSessionId(sessionId);
  loginSentFor = sessionId;

  if (isTagOpen("login")) return false;

  window.redeal?.("login", data);
  return true;
}

export function sendVoyadoOnsiteCheckout(order: GetOrderQuery["order"]) {
  if (!canCallRedeal() || !order) return;
  if (isTagOpen("checkout")) return;

  const name = [order.customerData?.firstName, order.customerData?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  window.redeal?.("checkout", {
    total: String(order.totalOrderValue ?? ""),
    revenue: String(order.totalOrderValueExVatExShipping ?? ""),
    currency: order.currencyCode ?? "",
    email: order.customerData?.email ?? undefined,
    coupons: order.couponCode ? [order.couponCode] : [],
    name: name || undefined,
    phone:
      order.customerData?.phoneNumber ??
      order.customerData?.cellPhoneNumber ??
      undefined,
  });
}

export function isCheckoutPath(pathname: string) {
  return CHECKOUT_PATH_RE.test(pathname);
}
